import { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  BuildingOffice2Icon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@components/ui';
import {
  CTA,
  FIELD_HINT,
  FORM_SKIN,
  GHOST,
  MODAL_BACKDROP,
  MODAL_BODY,
  MODAL_CALLOUT,
  MODAL_CLOSE_X,
  MODAL_FOOT,
  MODAL_HEAD,
  MODAL_ICON_TILE,
  MODAL_KICKER,
  MODAL_PANEL,
  MODAL_TITLE,
} from '@/components/ui/designTokens';
import { cn } from '@lib/utils/cn';
import { LocationPicker } from '@/components/maps/LocationPicker';
import { useCompanies } from '@/features/companies/hooks/useCompanies';
import {
  useProvinces,
  useDistricts,
  useTownships,
} from '@/features/customers/hooks/useGeolocation';
import {
  getFriendlyErrorMessage,
  useGeolocationByCoordinates,
  useCreateAddress,
  useCreateBranchOffice,
} from '../hooks/useBranchOffice';
import { useUpdateBranchOffice } from '@/features/branchOffice/hooks/useBranchOffices';
import { branchOfficeService } from '../services/branchOfficeService';
import type { GeolocationData, BranchOffice as BranchOfficeAPI } from '../types/branchOffice.types';
import type { BranchOffice } from '@/features/branchOffice/types/branchOffice.types';
// SUBSCRIPTIONS_DISABLED: import { useConsumption } from '@/features/subscriptions/hooks/useConsumption';
import Swal from 'sweetalert2';

/**
 * Convierte un error de axios/mutation en un mensaje amigable para el usuario,
 * evitando exponer textos crudos como "Request failed with status code 409".
 */

interface BranchOfficeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBranchOfficeCreated?: (branchOffice: BranchOfficeAPI) => void;
  branchOffice?: BranchOffice | null;
}

export function BranchOfficeFormModal({
  isOpen,
  onClose,
  onBranchOfficeCreated,
  branchOffice,
}: BranchOfficeFormModalProps) {
  const isEditMode = !!branchOffice;

  // Form data
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [geolocationData, setGeolocationData] = useState<GeolocationData | null>(null);
  const [selectedProvinceId, setSelectedProvinceId] = useState('');
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const [selectedTownshipId, setSelectedTownshipId] = useState('');
  const [addressDetail, setAddressDetail] = useState('');
  const [locationCode, setLocationCode] = useState(''); // Código de ubicación fiscal
  // Si falla la consulta del código fiscal, el mensaje se muestra DENTRO del
  // recuadro "Código de Ubicación Fiscal" (en rojo) en vez de un Swal aparte
  // — así el error queda pegado al campo al que se refiere.
  const [fiscalCodeError, setFiscalCodeError] = useState('');
  // Estado de carga PROPIO de la llamada al código fiscal — geolocationMutation
  // solo cubre el paso previo (coordenadas → provincia/distrito/corregimiento)
  // y ya vuelve a `isPending: false` antes de que termine esta segunda llamada.
  const [isFiscalCodeLoading, setIsFiscalCodeLoading] = useState(false);
  // Homologado con Step1CompanyData del wizard de compañía: sin modal de
  // "Campos incompletos" — al intentar guardar con datos faltantes, cada
  // campo vacío muestra su propio error debajo (como error={errors.x} de
  // react-hook-form). Antes de este primer intento no se muestra nada.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isActive, setIsActive] = useState(true);
  // Address ID for updating existing addresses
  const [currentAddressId, setCurrentAddressId] = useState<string | null>(null);

  // Hooks
  const { selectedCompany } = useCompanies({ navigateOnSelect: false });
  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();
  const { data: districts, isLoading: isLoadingDistricts } = useDistricts(selectedProvinceId);
  const { data: townships, isLoading: isLoadingTownships } = useTownships(selectedDistrictId);
  /* SUBSCRIPTIONS_DISABLED
  // Plan validation
  const { validateCanAddBranchOffice } = useConsumption();
  */

  const geolocationMutation = useGeolocationByCoordinates();
  const createAddressMutation = useCreateAddress();
  const createBranchOfficeMutation = useCreateBranchOffice();
  const updateBranchOfficeMutation = useUpdateBranchOffice();

  // State to track pending IDs while waiting for dropdown data to load
  const [pendingDistrictId, setPendingDistrictId] = useState<string | null>(null);
  const [pendingTownshipId, setPendingTownshipId] = useState<string | null>(null);

  // Populate form when editing - fetch full details from Aludra API
  useEffect(() => {
    const loadBranchOfficeDetails = async () => {
      if (isEditMode && branchOffice && selectedCompany?.id) {
        try {
          // Fetch complete branch office details from Aludra API
          const details = await branchOfficeService.getBranchOfficeDetails(
            selectedCompany.id,
            branchOffice.id
          );

          console.log('📋 Full branch office details loaded:', details);

          // Set basic fields
          setCode(details.Code || branchOffice.code);
          setDescription(details.Description || branchOffice.name);
          setEmail(details.EmailBranchOffice || branchOffice.email || '');
          setIsActive(details.IsActive ?? branchOffice.isActive);
          setAddressDetail(details.Address_AddressDetail || branchOffice.address || '');
          setLocationCode(details.Address_FiscalCode || branchOffice.fiscalCode || '');

          // Save the AddressId for updating
          if (details.AddressId) {
            setCurrentAddressId(details.AddressId);
            console.log('📍 Saved AddressId for updates:', details.AddressId);
          }

          // In edit mode, don't load coordinates - they may not match the address
          // User can optionally select new coordinates via the map if needed
          // Coordinates will only be updated if user explicitly selects a new location

          // Set geolocation data with IDs from Aludra
          setGeolocationData({
            Country: details.Address_CountryName || branchOffice.country || 'Panamá',
            CountryId: details.Address_CountryId || '',
            Province: details.Address_ProvinceName || branchOffice.province || '',
            ProvinceId: details.Address_ProvinceId || '',
            District: details.Address_DistrictName || branchOffice.district || '',
            DistrictId: details.Address_DistrictId || '',
            Township: details.Address_TownshipName || branchOffice.township || '',
            TownshipId: details.Address_TownshipId || '',
          } as GeolocationData);

          // Set province first, then save district and township IDs to set after data loads
          console.log('🏢 Setting location IDs from Aludra:', {
            provinceId: details.Address_ProvinceId,
            districtId: details.Address_DistrictId,
            townshipId: details.Address_TownshipId,
          });

          if (details.Address_ProvinceId) {
            // Normalize to lowercase since dropdown IDs are lowercase
            const provinceId = details.Address_ProvinceId.toLowerCase();
            setSelectedProvinceId(provinceId);
            console.log('✅ Setting province ID (normalized):', provinceId);

            // Save district and township IDs to set once their data loads
            if (details.Address_DistrictId) {
              setPendingDistrictId(details.Address_DistrictId.toLowerCase());
              console.log('📍 Set pendingDistrictId (normalized):', details.Address_DistrictId.toLowerCase());
            }
            if (details.Address_TownshipId) {
              setPendingTownshipId(details.Address_TownshipId.toLowerCase());
              console.log('📍 Set pendingTownshipId (normalized):', details.Address_TownshipId.toLowerCase());
            }
          }

        } catch (error) {
          console.error('Error loading branch office details:', error);
          // Fallback to basic data from props
          setCode(branchOffice.code);
          setDescription(branchOffice.name);
          setEmail(branchOffice.email || '');
          setIsActive(branchOffice.isActive);
          setAddressDetail(branchOffice.address || '');
          setLocationCode(branchOffice.fiscalCode || '');

          // Don't load coordinates in edit mode - they may not match the address

          setGeolocationData({
            Country: branchOffice.country || 'Panamá',
            CountryId: '',
            Province: branchOffice.province || '',
            ProvinceId: '',
            District: branchOffice.district || '',
            DistrictId: '',
            Township: branchOffice.township || '',
            TownshipId: '',
          } as GeolocationData);
        }
      }
    };

    if (isOpen && isEditMode) {
      loadBranchOfficeDetails();
    }
  }, [isEditMode, branchOffice, selectedCompany?.id, isOpen]);

  // Set pending district ID once districts are loaded
  useEffect(() => {
    if (pendingDistrictId && districts && districts.length > 0) {
      console.log('🔍 Checking district - pendingDistrictId:', pendingDistrictId);
      // IDs are already normalized to lowercase
      const districtExists = districts.some((d: any) => d.DistrictId === pendingDistrictId);
      if (districtExists) {
        console.log('✅ Setting district ID:', pendingDistrictId);
        setSelectedDistrictId(pendingDistrictId);
        setPendingDistrictId(null);
      } else {
        console.log('❌ District ID not found in list:', pendingDistrictId);
        console.log('Available IDs:', districts.map((d: any) => d.DistrictId));
      }
    }
  }, [districts, pendingDistrictId]);

  // Set pending township ID once townships are loaded
  useEffect(() => {
    if (pendingTownshipId && townships && townships.length > 0) {
      console.log('🔍 Checking township - pendingTownshipId:', pendingTownshipId);
      // IDs are already normalized to lowercase
      const townshipExists = townships.some((t: any) => t.TownshipId === pendingTownshipId);
      if (townshipExists) {
        console.log('✅ Setting township ID:', pendingTownshipId);
        setSelectedTownshipId(pendingTownshipId);
        setPendingTownshipId(null);
      } else {
        console.log('❌ Township ID not found in list:', pendingTownshipId);
        console.log('Available IDs:', townships.map((t: any) => t.TownshipId));
      }
    }
  }, [townships, pendingTownshipId]);

  const [coordinateError, setCoordinateError] = useState('');

  // Campos de texto de latitud/longitud, siempre visibles: si el buscador o
  // el clic en el mapa no encuentran el punto exacto, el usuario los escribe
  // a mano. Van como string separado (no directo `coordinates`) para poder
  // escribir libremente (signo, punto decimal a medio tipear, etc.) sin que
  // cada tecla dispare una validación/geolocalización a medio terminar.
  const [latitudeInput, setLatitudeInput] = useState('');
  const [longitudeInput, setLongitudeInput] = useState('');

  // Mantiene los inputs de texto sincronizados cuando las coordenadas
  // cambian por otra vía (clic en el mapa, búsqueda de dirección).
  useEffect(() => {
    if (coordinates) {
      setLatitudeInput(coordinates.lat.toFixed(6));
      setLongitudeInput(coordinates.lng.toFixed(6));
    }
  }, [coordinates]);

  const handleLocationSelect = (latitude: number, longitude: number, address?: string) => {
    setCoordinates({ lat: latitude, lng: longitude });
    if (address) {
      setAddressDetail(address);
    }

    // Validar coordenadas dentro de Panamá (DGI B304/B304a)
    if (latitude < 7 || latitude > 9.5) {
      setCoordinateError('Latitud fuera del rango de Panamá (7° a 9.5°)');
      return;
    }
    if (longitude < -83 || longitude > -77) {
      setCoordinateError('Longitud fuera del rango de Panamá (-83° a -77°)');
      return;
    }
    setCoordinateError('');

    // Sin wizard de pasos: apenas se elige un punto válido en el mapa (o se
    // escriben lat/lng a mano) se dispara la geolocalización, así el
    // formulario de la derecha se completa solo.
    handleFetchGeolocation(latitude, longitude);
  };

  // Confirma lat/lng tipeados a mano (onBlur, no en cada tecla) y reusa la
  // misma validación/geolocalización que un clic en el mapa.
  const handleManualCoordinatesCommit = () => {
    const lat = parseFloat(latitudeInput);
    const lng = parseFloat(longitudeInput);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    if (coordinates && coordinates.lat === lat && coordinates.lng === lng) return;
    handleLocationSelect(lat, lng);
  };

  const handleCodeChange = (value: string) => {
    // Allow only numeric characters and limit to 4 characters
    const numeric = value.replace(/[^0-9]/g, '');
    const truncated = numeric.substring(0, 4);
    setCode(truncated);

    // Validate
    if (truncated.length === 0) {
      setCodeError('El código es requerido');
    } else if (truncated.length < 4) {
      setCodeError('El código debe tener exactamente 4 dígitos numéricos');
    } else {
      setCodeError('');
    }
  };

  const handleFetchGeolocation = async (lat: number, lng: number) => {
    try {
      const data = await geolocationMutation.mutateAsync({
        latitude: lat.toString(),
        longitude: lng.toString(),
      });

      console.log('=== Geolocation Data Received ===');
      console.log('Full data:', data);

      setGeolocationData(data);

      // Clear pending IDs so they don't overwrite the new geolocation data
      setPendingDistrictId(null);
      setPendingTownshipId(null);

      // Pre-populate the form with geolocation data IDs
      if (data.ProvinceId) setSelectedProvinceId(data.ProvinceId);
      if (data.DistrictId) setSelectedDistrictId(data.DistrictId);
      if (data.TownshipId) setSelectedTownshipId(data.TownshipId);

      // Obtener código de ubicación fiscal
      if (data.Province && data.District && data.Township) {
        try {
          console.log('📍 Obteniendo código de ubicación fiscal...');
          console.log('Parámetros enviados a la API:', {
            province: data.Province,
            district: data.District,
            township: data.Township,
          });

          setIsFiscalCodeLoading(true);
          setFiscalCodeError('');
          const fiscalCode = await branchOfficeService.getFiscalCode(
            data.Province,
            data.District,
            data.Township
          );

          console.log('✅ Respuesta completa de fiscal code:', fiscalCode);
          console.log('✅ Tipo de respuesta:', typeof fiscalCode);
          setLocationCode(fiscalCode);
          setFiscalCodeError('');
        } catch (error: any) {
          console.error('❌ Error obteniendo código de ubicación:', error);
          console.error('❌ Detalles del error:', {
            message: error?.message,
            response: error?.response?.data,
            status: error?.response?.status,
          });
          setLocationCode('');
          setFiscalCodeError('No se pudo obtener el código de ubicación. Por favor, inténtelo nuevamente.');
        } finally {
          setIsFiscalCodeLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching geolocation:', error);
    }
  };

  const handleSubmit = async () => {
    console.log(`=== Branch Office ${isEditMode ? 'Update' : 'Create'} ===`);
    console.log('Company ID:', selectedCompany?.id);
    console.log('Is Edit Mode:', isEditMode);
    console.log('Branch Office ID:', branchOffice?.id);

    // Validate email format
    if (emailError) {
      Swal.fire({
        title: 'Errores de validación',
        text: 'Por favor corrija los errores en el formulario antes de continuar',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (!selectedCompany?.id) {
      console.error('Missing company ID');
      Swal.fire({
        title: 'Error',
        text: 'No se pudo obtener el ID de la compañía',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    /* SUBSCRIPTIONS_DISABLED
    // Plan validation for branch offices
    if (!isEditMode) {
      const validation = validateCanAddBranchOffice();

      if (!validation.allowed) {
        const result = await Swal.fire({
          title: 'Límite de sucursales alcanzado',
          text: validation.reason,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Ver planes',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#3085d6',
        });

        if (result.isConfirmed) {
          // Navigate to plans page
          window.location.href = '/subscriptions/plans';
        }
        return;
      }
    }
    */

    // For edit mode, update branch office and address if needed
    if (isEditMode && branchOffice) {
      try {
        // Step 1: Update address if we have an AddressId and location data
        if (currentAddressId && selectedProvinceId && selectedDistrictId && selectedTownshipId) {
          const addressPayload: any = {
            AddressId: currentAddressId,
            AddressDetail: addressDetail,
            CountryId: geolocationData?.CountryId || 'PA',
            ProvinceId: selectedProvinceId,
            DistrictId: selectedDistrictId,
            TownshipId: selectedTownshipId,
            AddressTypeId: '42ACBCF4-BE83-4F03-8B5E-5760FC92B59B',
            AddressFiscalCode: locationCode,
          };

          // Solo incluir coordenadas si el usuario seleccionó una nueva ubicación
          if (coordinates) {
            addressPayload.Longitude = coordinates.lng.toString();
            addressPayload.Latitude = coordinates.lat.toString();
          }

          console.log('📍 Updating address with payload:', addressPayload);
          await branchOfficeService.updateAddress(addressPayload);
          console.log('✅ Address updated successfully');
        }

        // Step 2: Update branch office
        const updatePayload = {
          BranchOfficeId: branchOffice.id,
          CompanyId: selectedCompany.id,
          CountryId: null,
          Code: code,
          Description: description,
          IsActive: isActive,
          EmailBranchOffice: email, // Aludra expects EmailBranchOffice
          AddressId: currentAddressId || undefined,
          AddressFiscalCode: locationCode || branchOffice.fiscalCode,
        };

        console.log('🏢 Update branch office payload:', updatePayload);

        await updateBranchOfficeMutation.mutateAsync({
          id: branchOffice.id,
          data: updatePayload as any,
        });

        Swal.fire({
          title: 'Éxito',
          text: 'Sucursal actualizada exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
        });
        handleClose();
        // Don't call onBranchOfficeCreated in edit mode since it expects a different type
        return;
      } catch (error: any) {
        console.error('Error updating branch office:', error);
        const errorMessage = getFriendlyErrorMessage(error);
        Swal.fire({
          title: 'Error al actualizar la sucursal',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'Aceptar',
        });
        return;
      }
    }

    // For create mode, validate all required fields
    if (!coordinates) {
      console.error('Missing coordinates');
      Swal.fire({
        title: 'Error',
        text: 'No se encontraron las coordenadas. Por favor seleccione una ubicación en el mapa.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (!geolocationData) {
      console.error('Missing geolocation data');
      Swal.fire({
        title: 'Error',
        text: 'No se encontraron los datos de geolocalización.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (!locationCode) {
      console.error('Missing location code');
      Swal.fire({
        title: 'Error',
        text: 'No se pudo obtener el código de ubicación fiscal.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    try {
      // Step 1: Create address with the correct AddressTypeId GUID and AddressFiscalCode
      console.log('Creating address...');
      console.log('Address payload:', {
        AddressDetail: addressDetail,
        CountryId: geolocationData.CountryId,
        ProvinceId: selectedProvinceId,
        DistrictId: selectedDistrictId,
        TownshipId: selectedTownshipId,
        Longitude: coordinates.lng.toString(),
        Latitude: coordinates.lat.toString(),
        AddressTypeId: '42ACBCF4-BE83-4F03-8B5E-5760FC92B59B', // GUID correcto (hardcoded)
        AddressFiscalCode: locationCode, // Código de ubicación fiscal obtenido previamente
      });

      const addressResponse = await createAddressMutation.mutateAsync({
        AddressDetail: addressDetail,
        CountryId: geolocationData.CountryId,
        ProvinceId: selectedProvinceId,
        DistrictId: selectedDistrictId,
        TownshipId: selectedTownshipId,
        Longitude: coordinates.lng.toString(),
        Latitude: coordinates.lat.toString(),
        AddressTypeId: '42ACBCF4-BE83-4F03-8B5E-5760FC92B59B', // GUID correcto (hardcoded)
        AddressFiscalCode: locationCode, // Código de ubicación fiscal obtenido previamente
      });

      console.log('Address created successfully!');
      console.log('Full address response:', JSON.stringify(addressResponse, null, 2));
      console.log('AddressId extracted:', addressResponse.AddressId);
      console.log('AddressFiscalCode in response:', addressResponse.AddressFiscalCode);

      // Validate that we got an AddressId
      if (!addressResponse.AddressId) {
        console.error('ERROR: No AddressId received from address creation!');
        console.error('Response keys:', Object.keys(addressResponse));
        throw new Error('No se recibió AddressId de la creación de dirección');
      }

      console.log('===========================================');
      console.log('🚀 CREATING BRANCH OFFICE - PAYLOAD DETAILS');
      console.log('===========================================');

      const branchOfficePayload = {
        CompanyId: selectedCompany.id,
        Code: code,
        Description: description,
        IsActive: isActive,
        EmailBranchOffice: email, // Aludra expects EmailBranchOffice
        AddressId: addressResponse.AddressId,
        addressFiscalCode: locationCode, // Código de ubicación fiscal
      };

      console.log('Full payload object:', branchOfficePayload);
      console.log('Payload as JSON string:', JSON.stringify(branchOfficePayload, null, 2));
      console.log('Individual field values:');
      console.log('  - CompanyId:', selectedCompany.id, '(type:', typeof selectedCompany.id, ')');
      console.log('  - Code:', code, '(type:', typeof code, ', length:', code?.length, ')');
      console.log('  - Description:', description, '(type:', typeof description, ', length:', description?.length, ')');
      console.log('  - IsActive:', isActive, '(type:', typeof isActive, ')');
      console.log('  - EmailBranchOffice:', email, '(type:', typeof email, ', length:', email?.length, ')');
      console.log('  - AddressId:', addressResponse.AddressId, '(type:', typeof addressResponse.AddressId, ')');
      console.log('  - addressFiscalCode:', locationCode, '(type:', typeof locationCode, ')');
      console.log('===========================================');

      // Step 3: Create branch office
      const branchOffice = await createBranchOfficeMutation.mutateAsync(branchOfficePayload);

      console.log('Branch office created:', branchOffice);

      if (onBranchOfficeCreated) {
        onBranchOfficeCreated(branchOffice);
      }

      handleClose();
    } catch (error: any) {
      console.error('Error creating branch office:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
      });
      // Sin diálogo acá a propósito: el `onError` de `useCreateBranchOffice` ya
      // abre el modal de error del portal, con este mismo mensaje amigable
      // (la traducción se movió al hook). Antes salían los dos apilados.
    }
  };

  const handleClose = () => {
    // Reset form
    setCoordinates(null);
    setLatitudeInput('');
    setLongitudeInput('');
    setCoordinateError('');
    setGeolocationData(null);
    setSelectedProvinceId('');
    setSelectedDistrictId('');
    setSelectedTownshipId('');
    setPendingDistrictId(null);
    setPendingTownshipId(null);
    setCurrentAddressId(null);
    setAddressDetail('');
    setLocationCode('');
    setFiscalCodeError('');
    setIsFiscalCodeLoading(false);
    setSubmitAttempted(false);
    setCode('');
    setCodeError('');
    setDescription('');
    setEmail('');
    setEmailError('');
    setIsActive(true);
    onClose();
  };

  // En edición se puede omitir la selección en el mapa (las coordenadas son
  // opcionales); en creación son obligatorias.
  const isCoordinatesValid = isEditMode || coordinates !== null;
  const isDetailsValid =
    geolocationData?.CountryId &&
    selectedProvinceId &&
    selectedDistrictId &&
    selectedTownshipId &&
    addressDetail &&
    locationCode &&
    code &&
    code.length === 4 &&
    !codeError &&
    description;
  const isFormValid = isCoordinatesValid && isDetailsValid;

  /**
   * Al intentar guardar con campos vacíos, lleva la vista (scroll + foco) al
   * primer campo inválido — el equivalente "casero" al `reportValidity()`
   * nativo del navegador (el que hace scroll solo y muestra "rellena este
   * campo"), que acá no se dispara porque el botón es `type="button"` sin
   * `<form>` detrás. Sin esto, con el modal partido en dos mitades y varios
   * campos, un error que cae fuera de la parte visible pasaba desapercibido
   * — el mensaje en rojo estaba, pero nadie lo veía sin bajar a buscarlo.
   */
  const focusFirstInvalidField = () => {
    const camposEnOrden = [
      { invalido: !isCoordinatesValid, testId: 'branch-office-form-modal-latitude-input' },
      { invalido: !selectedProvinceId, testId: 'branch-office-form-modal-province-select' },
      { invalido: !selectedDistrictId, testId: 'branch-office-form-modal-district-select' },
      { invalido: !selectedTownshipId, testId: 'branch-office-form-modal-township-select' },
      { invalido: !addressDetail.trim(), testId: 'branch-office-form-modal-address-detail-input' },
      { invalido: !locationCode, testId: 'branch-office-form-modal-fiscal-code-input' },
      { invalido: !code || code.length !== 4 || !!codeError, testId: 'branch-office-form-modal-code-input' },
      { invalido: !description.trim(), testId: 'branch-office-form-modal-description-input' },
    ];
    const primerInvalido = camposEnOrden.find((c) => c.invalido);
    if (!primerInvalido) return;

    const el = document.querySelector<HTMLElement>(`[data-testid="${primerInvalido.testId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // El recuadro del código fiscal no es un input enfocable — solo scrollea.
    if (el && typeof (el as HTMLInputElement).focus === 'function') {
      (el as HTMLInputElement).focus({ preventScroll: true });
    }
  };

  // Debug validation and dropdown data
  console.log('=== Form Debug ===');
  console.log('Validation:', {
    isDetailsValid,
    country: geolocationData?.Country,
    countryId: geolocationData?.CountryId,
    selectedProvinceId: selectedProvinceId,
    selectedDistrictId: selectedDistrictId,
    selectedTownshipId: selectedTownshipId,
    addressDetail: addressDetail,
    locationCode: locationCode,
    code: code,
    description: description,
  });
  console.log('Dropdown data:', {
    provinces: provinces?.length,
    districts: districts?.length,
    townships: townships?.length,
  });
  if (provinces && provinces.length > 0) {
    console.log('Sample province:', provinces[0]);
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={MODAL_BACKDROP} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={cn('flex max-h-[calc(100vh-2rem)] max-w-6xl flex-col', MODAL_PANEL)}
              >
                {/* ---------- HEAD ---------- */}
                <div className={cn(MODAL_HEAD, 'shrink-0')}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-3.5">
                      <span className={MODAL_ICON_TILE}>
                        <BuildingOffice2Icon />
                      </span>
                      <div className="min-w-0">
                        <div className={MODAL_KICKER}>
                          {isEditMode && code ? `Sucursal · ${code}` : 'Administración · Sucursal'}
                        </div>
                        <Dialog.Title as="h3" className={cn(MODAL_TITLE, 'mt-1')}>
                          {isEditMode ? 'Editar Sucursal' : 'Crear Nueva Sucursal'}
                        </Dialog.Title>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleClose}
                      aria-label="Cerrar"
                      data-testid="branch-office-form-modal-close-button"
                      className={MODAL_CLOSE_X}
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {/* ---------- BODY ---------- */}
                {/* Mapa y formulario van lado a lado (mitad y mitad) en vez de
                    ser dos pasos secuenciales: al hacer clic en el mapa se
                    dispara la geolocalización y el formulario de la derecha
                    se autocompleta sin salir de la pantalla. */}
                <div className={cn(MODAL_BODY, 'min-h-0 flex-1', FORM_SKIN)}>
                  <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-2">
                    {/* Mitad izquierda: mapa */}
                    <div className="space-y-4">
                      <div className="inline-flex items-center gap-2.5 text-[12px] font-bold uppercase tracking-[0.24em] text-teal-500">
                        <span aria-hidden="true" className="h-px w-[22px] bg-teal-500" />
                        Ubicación geográfica
                      </div>
                      <div>
                        <h3 className="text-lg font-extrabold tracking-[-0.015em] text-ink-850 dark:text-white">
                          ¿Dónde está ubicada la sucursal?
                        </h3>
                        <p className="mt-1.5 text-[14px] text-secondary-500 dark:text-secondary-400">
                          Haga clic en el mapa para seleccionar la ubicación
                          {isEditMode ? ' (opcional si no cambia de dirección)' : ''}. El código fiscal se genera
                          automáticamente.
                        </p>
                      </div>
                      <div className="overflow-hidden rounded-[14px] border border-[#ececec] dark:border-secondary-700">
                        <LocationPicker
                          onLocationSelect={handleLocationSelect}
                          position={coordinates}
                          height="420px"
                          // Buscador de direcciones (Places Autocomplete)
                          // activo — mismo que ya usa Step2FiscalAddress.tsx
                          // (alta de compañía).
                          showSearch
                        />
                      </div>

                      {/* Latitud/Longitud siempre visibles y editables: si el
                          mapa no encuentra el punto exacto, se escriben a
                          mano y valen igual que un clic (misma validación y
                          geolocalización automática). */}
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          label="Latitud"
                          value={latitudeInput}
                          onChange={(e) => setLatitudeInput(e.target.value)}
                          onBlur={handleManualCoordinatesCommit}
                          placeholder="Ej: 9.076500"
                          inputMode="decimal"
                          data-testid="branch-office-form-modal-latitude-input"
                        />
                        <Input
                          label="Longitud"
                          value={longitudeInput}
                          onChange={(e) => setLongitudeInput(e.target.value)}
                          onBlur={handleManualCoordinatesCommit}
                          placeholder="Ej: -79.530900"
                          inputMode="decimal"
                          data-testid="branch-office-form-modal-longitude-input"
                        />
                      </div>
                      {geolocationMutation.isPending && (
                        <p className="text-[13px] text-secondary-500 dark:text-secondary-400">
                          Resolviendo provincia/distrito/corregimiento…
                        </p>
                      )}
                      {coordinateError && (
                        <p className="text-[13px] text-danger-600 dark:text-danger-400" data-testid="branch-office-form-modal-coordinate-error">{coordinateError}</p>
                      )}
                      {!coordinateError && submitAttempted && !isCoordinatesValid && (
                        <p className="text-[13px] text-danger-600 dark:text-danger-400" data-testid="branch-office-form-modal-coordinate-error">
                          Seleccione la ubicación de la sucursal en el mapa
                        </p>
                      )}

                      {/* Estado de la sucursal — movido a esta mitad. */}
                      <label
                        htmlFor="isActive"
                        className="grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3.5 rounded-xl border border-[#eef0f2] bg-[#fbfbfc] px-[18px] py-4 dark:border-secondary-700 dark:bg-secondary-900"
                      >
                        <input
                          type="checkbox"
                          id="isActive"
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                          className="h-[18px] w-[18px] rounded-[6px] border-secondary-300 text-[#0d9488] focus:ring-teal-500/40"
                         data-testid="branch-office-form-modal-active-checkbox"/>
                        <span>
                          <span className="block text-sm font-bold text-ink-850 dark:text-white">
                            Sucursal activa
                          </span>
                          <span className="mt-0.5 block text-xs text-secondary-500 dark:text-secondary-400">
                            Disponible para emitir documentos desde esta sucursal.
                          </span>
                        </span>
                        <span
                          className={cn(
                            'font-mono text-[11px] font-bold uppercase tracking-[0.18em]',
                            isActive ? 'text-[#0d9488]' : 'text-secondary-400'
                          )}
                        >
                          Estado · {isActive ? 'Activa' : 'Inactiva'}
                        </span>
                      </label>
                    </div>

                    {/* Mitad derecha: dirección y datos de la sucursal */}
                    <div className="space-y-[18px]">
                      <div>
                        <div className="inline-flex items-center gap-2.5 text-[12px] font-bold uppercase tracking-[0.24em] text-teal-500">
                          <span aria-hidden="true" className="h-px w-[22px] bg-teal-500" />
                          Dirección fiscal
                        </div>
                        <h3 className="mt-2.5 text-lg font-extrabold tracking-[-0.015em] text-ink-850 dark:text-white">
                          Datos de la sucursal
                        </h3>
                        <p className="mt-1.5 text-[14px] text-secondary-500 dark:text-secondary-400">
                          Se completan solos al elegir la ubicación; también puede ajustarlos manualmente.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Country - Read only from geolocation */}
                        <Input
                          label="País"
                          value={geolocationData?.Country || ''}
                          disabled
                          required
                         data-testid="branch-office-form-modal-country-input"/>

                        {/* Province */}
                        <Select
                          label="Provincia"
                          value={selectedProvinceId}
                          onChange={(e) => {
                            setSelectedProvinceId(e.target.value);
                            setSelectedDistrictId('');
                            setSelectedTownshipId('');
                          }}
                          disabled={isLoadingProvinces}
                          error={submitAttempted && !selectedProvinceId ? 'La provincia es requerida' : undefined}
                          required data-testid="branch-office-form-modal-province-select"
                        >
                          <option value="">Seleccione una provincia</option>
                          {provinces?.map((province: any) => (
                            <option key={province.ProvinceId} value={province.ProvinceId}>
                              {province.Name}
                            </option>
                          ))}
                        </Select>

                        {/* District */}
                        <Select
                          label="Distrito"
                          value={selectedDistrictId}
                          onChange={(e) => {
                            setSelectedDistrictId(e.target.value);
                            setSelectedTownshipId('');
                          }}
                          disabled={!selectedProvinceId || isLoadingDistricts}
                          error={submitAttempted && !selectedDistrictId ? 'El distrito es requerido' : undefined}
                          required data-testid="branch-office-form-modal-district-select"
                        >
                          <option value="">Seleccione un distrito</option>
                          {districts?.map((district: any) => (
                            <option key={district.DistrictId} value={district.DistrictId}>
                              {district.Name}
                            </option>
                          ))}
                        </Select>

                        {/* Township (Corregimiento) */}
                        <Select
                          label="Corregimiento"
                          value={selectedTownshipId}
                          onChange={async (e) => {
                            const newTownshipId = e.target.value;
                            setSelectedTownshipId(newTownshipId);

                            // Recalculate fiscal code when township changes
                            if (newTownshipId && selectedProvinceId && selectedDistrictId) {
                              const selectedProvince = provinces?.find((p: any) => p.ProvinceId === selectedProvinceId);
                              const selectedDistrict = districts?.find((d: any) => d.DistrictId === selectedDistrictId);
                              const selectedTownship = townships?.find((t: any) => t.TownshipId === newTownshipId);

                              if (selectedProvince && selectedDistrict && selectedTownship) {
                                setIsFiscalCodeLoading(true);
                                setFiscalCodeError('');
                                try {
                                  console.log('🔄 Recalculando código fiscal para:', {
                                    province: selectedProvince.Name,
                                    district: selectedDistrict.Name,
                                    township: selectedTownship.Name,
                                  });
                                  const fiscalCode = await branchOfficeService.getFiscalCode(
                                    selectedProvince.Name,
                                    selectedDistrict.Name,
                                    selectedTownship.Name
                                  );
                                  console.log('✅ Nuevo código fiscal:', fiscalCode);
                                  setLocationCode(fiscalCode);
                                  setFiscalCodeError('');
                                } catch (error) {
                                  console.error('❌ Error obteniendo código fiscal:', error);
                                  setLocationCode('');
                                  setFiscalCodeError('No se pudo obtener el código de ubicación fiscal.');
                                } finally {
                                  setIsFiscalCodeLoading(false);
                                }
                              }
                            }
                          }}
                          disabled={!selectedDistrictId || isLoadingTownships}
                          error={submitAttempted && !selectedTownshipId ? 'El corregimiento es requerido' : undefined}
                          required data-testid="branch-office-form-modal-township-select"
                        >
                          <option value="">Seleccione un corregimiento</option>
                          {townships?.map((township: any) => (
                            <option key={township.TownshipId} value={township.TownshipId}>
                              {township.Name}
                            </option>
                          ))}
                        </Select>
                      </div>

                      {/* Address Detail — label homologado con "Dirección" del
                          paso Dirección fiscal del wizard de compañía
                          (Step2FiscalAddress.tsx). Sigue siendo un solo campo
                          (no se separó "Casa/Oficina/Apartamento" aparte). */}
                      <Input
                        label="Dirección"
                        value={addressDetail}
                        onChange={(e) => setAddressDetail(e.target.value)}
                        placeholder="Ej: Edificio X, Piso 5, Oficina 501"
                        error={submitAttempted && !addressDetail.trim() ? 'La dirección es requerida' : undefined}
                        required
                       data-testid="branch-office-form-modal-address-detail-input"/>

                      {/*
                        Codigo fiscal: el diseño lo saca del input gris y lo pone
                        como dato destacado del bloque. Sigue siendo de solo
                        lectura y sigue viniendo del mismo estado (`locationCode`).
                        Tres estados dentro del mismo recuadro: cargando,
                        error (homologado con el resto de mensajes de error del
                        formulario — sin negrita, mismo tono que un error de
                        Input) y el código resuelto.
                      */}
                      <div className={cn(MODAL_CALLOUT, 'p-6')}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div
                              className={cn(
                                'inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em]',
                                fiscalCodeError ? 'text-danger-600 dark:text-danger-400' : 'text-[#0d9488]'
                              )}
                            >
                              {fiscalCodeError ? (
                                <ExclamationTriangleIcon className="h-3 w-3" />
                              ) : (
                                <ShieldCheckIcon className="h-3 w-3" />
                              )}
                              Código de Ubicación Fiscal <span className="text-[#b8503a]">*</span>
                            </div>

                            <div data-testid="branch-office-form-modal-fiscal-code-input" className="mt-2.5">
                              {isFiscalCodeLoading ? (
                                <div className="flex items-center gap-2 text-[14px] font-medium text-secondary-500 dark:text-secondary-400">
                                  <Spinner size="sm" />
                                  Buscando código de ubicación fiscal…
                                </div>
                              ) : fiscalCodeError ? (
                                <p className="text-sm font-medium leading-snug text-danger-600 dark:text-danger-400">
                                  {fiscalCodeError}
                                </p>
                              ) : (
                                <div className="font-mono text-[30px] font-extrabold tracking-[0.06em] text-ink-850 dark:text-white">
                                  {locationCode || '—'}
                                </div>
                              )}
                            </div>

                            {!fiscalCodeError && !isFiscalCodeLoading && (
                              submitAttempted && !locationCode ? (
                                <p className="mt-2 text-sm font-medium leading-snug text-danger-600 dark:text-danger-400">
                                  El código de ubicación fiscal es requerido
                                </p>
                              ) : (
                                <div className={cn(FIELD_HINT, 'mt-2')}>
                                  Generado automáticamente según la ubicación seleccionada
                                </div>
                              )
                            )}
                          </div>
                          <div className="flex shrink-0 flex-col items-end gap-1">
                            <span className="font-mono text-[11px] tracking-[0.20em] text-secondary-400">
                              FORMATO DGI
                            </span>
                            <span className="font-mono text-[12px] font-bold text-[#6b239e]">
                              PROV · DIST · CORR
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Branch Office Details: separador simple, sin título
                          (ya no hace falta numerar "02" — dejó de ser un
                          segundo paso, es la misma pantalla). */}
                      <div className="border-t border-dashed border-[#eef0f2] pt-[22px] dark:border-secondary-700">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <Input
                            label="Código"
                            value={code}
                            onChange={(e) => handleCodeChange(e.target.value)}
                            placeholder="Ej: 0001"
                            maxLength={4}
                            error={codeError || (submitAttempted && !code ? 'El código es requerido' : '')}
                            helperText="4 dígitos numéricos (0-9)"
                            required
                           data-testid="branch-office-form-modal-code-input"/>

                          <Input
                            label="Descripción"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Ej: Sucursal Principal"
                            error={submitAttempted && !description.trim() ? 'La descripción es requerida' : undefined}
                            required
                           data-testid="branch-office-form-modal-description-input"/>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ---------- FOOT ----------
                    Mismos botones (clases GHOST/CTA) que "Crear Usuario"
                    (CreateUserModal): blanco+borde para Cancelar, navy sólido
                    para la acción primaria — sin el verde que traía
                    variant="success" del Button genérico. */}
                <div className={cn(MODAL_FOOT, 'shrink-0')}>
                  <div />
                  <div className="ml-auto flex gap-2.5">
                    <button
                      type="button"
                      onClick={handleClose}
                      data-testid="branch-office-form-modal-cancel-button"
                      disabled={
                        createAddressMutation.isPending ||
                        createBranchOfficeMutation.isPending ||
                        updateBranchOfficeMutation.isPending
                      }
                      className={GHOST}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      data-testid="branch-office-form-modal-submit-button"
                      onClick={() => {
                        console.log('Button clicked! isFormValid:', isFormValid);
                        setSubmitAttempted(true);
                        if (!isFormValid) {
                          console.error('Form validation failed:', {
                            coordinates,
                            country: geolocationData?.Country,
                            countryId: geolocationData?.CountryId,
                            selectedProvinceId,
                            selectedDistrictId,
                            selectedTownshipId,
                            addressDetail,
                            code,
                            description,
                          });
                          // Homologado con Step1CompanyData del wizard de
                          // compañía: sin Swal de "Campos incompletos" — cada
                          // campo vacío ya muestra su propio error inline
                          // (ver `error={...}` en los Input/Select de arriba).
                          // Lleva la vista al primero para que no pase
                          // desapercibido si quedó fuera de lo visible.
                          focusFirstInvalidField();
                          return;
                        }
                        handleSubmit();
                      }}
                      disabled={
                        createAddressMutation.isPending ||
                        createBranchOfficeMutation.isPending ||
                        updateBranchOfficeMutation.isPending
                      }
                      className={CTA}
                    >
                      {createAddressMutation.isPending || createBranchOfficeMutation.isPending || updateBranchOfficeMutation.isPending ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          {isEditMode ? 'Actualizando...' : 'Creando...'}
                        </>
                      ) : (
                        isEditMode ? 'Actualizar Sucursal' : 'Crear Sucursal'
                      )}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
